import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatJPY } from "@/lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Loader2, CheckCircle2, AlertTriangle, FileDown, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CAT_LABEL = { asset: "資産", liability: "負債", equity: "純資産", revenue: "収益", cost: "売上原価", expense: "費用" };
const nowY = new Date().getFullYear();
const YEARS = [nowY + 1, nowY, nowY - 1, nowY - 2, nowY - 3];

export default function Reports() {
  const [year, setYear] = useState(nowY);
  const [pl, setPL] = useState(null);
  const [bs, setBS] = useState(null);
  const [cf, setCF] = useState(null);
  const [tb, setTB] = useState(null);
  const [ms, setMS] = useState(null);
  const [ct, setCT] = useState(null);
  const [ctr, setCTR] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [ledgerAcc, setLedgerAcc] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/reports/profit-loss?year=${year}`),
      api.get(`/reports/balance-sheet?year=${year}`),
      api.get(`/reports/cash-flow?year=${year}`),
      api.get(`/reports/trial-balance?year=${year}`),
      api.get(`/reports/monthly-summary?year=${year}`),
      api.get(`/reports/consumption-tax?year=${year}`),
      api.get(`/reports/consumption-tax-return?year=${year}`),
    ]).then(([p, b, c, t, m, x, cr]) => {
      setPL(p.data); setBS(b.data); setCF(c.data); setTB(t.data); setMS(m.data); setCT(x.data); setCTR(cr.data);
    }).finally(() => setLoading(false));
  }, [year]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/accounts?active_only=true").then((r) => setAccounts(r.data || [])).catch(() => {}); }, []);

  const loadLedger = (name) => {
    setLedgerAcc(name); setLedger(null);
    if (!name) return;
    api.get(`/reports/general-ledger?account=${encodeURIComponent(name)}&year=${year}`).then((r) => setLedger(r.data)).catch(() => {});
  };

  const [exporting, setExporting] = useState("");
  const doExport = async (reportType, fmt, account) => {
    const key = `${reportType}-${fmt}`;
    setExporting(key);
    try {
      let url = `/reports/${reportType}/export?format=${fmt}&year=${year}`;
      if (account) url += `&account=${encodeURIComponent(account)}`;
      const res = await api.get(url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      let fname = `${reportType}.${fmt}`;
      const m = cd.match(/filename\*=UTF-8''([^;]+)/);
      if (m) { try { fname = decodeURIComponent(m[1]); } catch { /* keep */ } }
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("エクスポートに失敗しました");
    } finally {
      setExporting("");
    }
  };

  const ExportBtns = ({ reportType, account, disabled }) => (
    <div className="flex items-center gap-2" data-testid={`export-${reportType}`}>
      <Button variant="outline" size="sm" disabled={disabled || exporting === `${reportType}-pdf`}
        onClick={() => doExport(reportType, "pdf", account)} data-testid={`export-${reportType}-pdf`}>
        {exporting === `${reportType}-pdf` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
        PDF
      </Button>
      <Button variant="outline" size="sm" disabled={disabled || exporting === `${reportType}-xlsx`}
        onClick={() => doExport(reportType, "xlsx", account)} data-testid={`export-${reportType}-xlsx`}>
        {exporting === `${reportType}-xlsx` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
        Excel
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">レポート</div>
          <h1 className="mt-1 font-display font-bold text-3xl tracking-tight text-stone-900">財務レポート（仕訳ベース）</h1>
          <p className="mt-1 text-sm text-stone-500">試算表・総勘定元帳・損益計算書・貸借対照表・月次・キャッシュフロー</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-500">会計年度</span>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32" data-testid="report-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}年度</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-emerald-900" /></div>
      ) : (
        <Tabs defaultValue="tb">
          <TabsList className="bg-stone-100 flex-wrap h-auto" data-testid="reports-tabs">
            <TabsTrigger value="tb" data-testid="tab-tb">試算表</TabsTrigger>
            <TabsTrigger value="gl" data-testid="tab-gl">総勘定元帳</TabsTrigger>
            <TabsTrigger value="pl" data-testid="tab-pl">損益計算書 (P/L)</TabsTrigger>
            <TabsTrigger value="bs" data-testid="tab-bs">貸借対照表 (B/S)</TabsTrigger>
            <TabsTrigger value="ms" data-testid="tab-ms">月次サマリ</TabsTrigger>
            <TabsTrigger value="ct" data-testid="tab-ct">消費税集計</TabsTrigger>
            <TabsTrigger value="cf" data-testid="tab-cf">キャッシュフロー</TabsTrigger>
          </TabsList>

          {/* 試算表 */}
          <TabsContent value="tb" className="mt-6 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <BalancedBadge ok={tb?.balanced} />
              <ExportBtns reportType="trial-balance" disabled={!tb?.rows?.length} />
            </div>
            <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="trial-balance-table">
                <thead className="bg-stone-50 text-stone-500 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">コード</th><th className="text-left px-3 py-2">勘定科目</th>
                    <th className="text-left px-3 py-2">区分</th>
                    <th className="text-right px-3 py-2">借方合計</th><th className="text-right px-3 py-2">貸方合計</th>
                    <th className="text-right px-3 py-2">残高(借)</th><th className="text-right px-3 py-2">残高(貸)</th>
                  </tr>
                </thead>
                <tbody>
                  {tb?.rows?.length ? tb.rows.map((r, i) => (
                    <tr key={i} className="border-t border-stone-100" data-testid={`tb-row-${i}`}>
                      <td className="px-3 py-2 text-stone-400 font-numbers">{r.code || "—"}</td>
                      <td className="px-3 py-2 text-stone-800">{r.name}</td>
                      <td className="px-3 py-2"><span className="text-[11px] rounded-full bg-stone-100 px-2 py-0.5 text-stone-600">{CAT_LABEL[r.category] || r.category}</span></td>
                      <td className="px-3 py-2 text-right font-numbers">{formatJPY(r.total_debit)}</td>
                      <td className="px-3 py-2 text-right font-numbers">{formatJPY(r.total_credit)}</td>
                      <td className="px-3 py-2 text-right font-numbers text-stone-900">{r.balance_debit ? formatJPY(r.balance_debit) : "—"}</td>
                      <td className="px-3 py-2 text-right font-numbers text-stone-900">{r.balance_credit ? formatJPY(r.balance_credit) : "—"}</td>
                    </tr>
                  )) : <tr><td colSpan={7} className="text-center text-stone-400 py-10">仕訳データがありません</td></tr>}
                </tbody>
                {tb?.rows?.length ? (
                  <tfoot className="bg-stone-50 font-semibold">
                    <tr className="border-t-2 border-stone-300">
                      <td className="px-3 py-2" colSpan={3}>合計</td>
                      <td className="px-3 py-2 text-right font-numbers">{formatJPY(tb.totals.total_debit)}</td>
                      <td className="px-3 py-2 text-right font-numbers">{formatJPY(tb.totals.total_credit)}</td>
                      <td className="px-3 py-2 text-right font-numbers">{formatJPY(tb.totals.balance_debit)}</td>
                      <td className="px-3 py-2 text-right font-numbers">{formatJPY(tb.totals.balance_credit)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </TabsContent>

          {/* 総勘定元帳 */}
          <TabsContent value="gl" className="mt-6 space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-500">勘定科目</span>
              <Select value={ledgerAcc} onValueChange={loadLedger}>
                <SelectTrigger className="w-64" data-testid="ledger-account-select"><SelectValue placeholder="科目を選択" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.name}>{a.code} {a.name}</SelectItem>)}</SelectContent>
              </Select>
              {ledgerAcc && ledger ? <ExportBtns reportType="general-ledger" account={ledgerAcc} /> : null}
            </div>
            {!ledgerAcc ? (
              <p className="text-sm text-stone-400 py-10 text-center">勘定科目を選択すると総勘定元帳が表示されます</p>
            ) : !ledger ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-emerald-900" /></div>
            ) : (
              <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="ledger-table">
                  <thead className="bg-stone-50 text-stone-500 text-xs">
                    <tr><th className="text-left px-3 py-2">日付</th><th className="text-left px-3 py-2">摘要</th><th className="text-left px-3 py-2">相手科目</th><th className="text-right px-3 py-2">借方</th><th className="text-right px-3 py-2">貸方</th><th className="text-right px-3 py-2">残高</th></tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-stone-100 bg-amber-50/40"><td className="px-3 py-2" colSpan={5}>期首残高</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(ledger.opening)}</td></tr>
                    {ledger.lines.map((l, i) => (
                      <tr key={i} className="border-t border-stone-100" data-testid={`ledger-row-${i}`}>
                        <td className="px-3 py-2 font-numbers text-stone-500">{l.date}</td>
                        <td className="px-3 py-2 text-stone-800">{l.description}</td>
                        <td className="px-3 py-2 text-stone-500">{l.counterpart}</td>
                        <td className="px-3 py-2 text-right font-numbers">{l.debit ? formatJPY(l.debit) : "—"}</td>
                        <td className="px-3 py-2 text-right font-numbers">{l.credit ? formatJPY(l.credit) : "—"}</td>
                        <td className="px-3 py-2 text-right font-numbers text-stone-900">{formatJPY(l.balance)}</td>
                      </tr>
                    ))}
                    {!ledger.lines.length ? <tr><td colSpan={6} className="text-center text-stone-400 py-8">当期の仕訳がありません</td></tr> : null}
                  </tbody>
                  <tfoot className="bg-stone-50 font-semibold"><tr className="border-t-2 border-stone-300"><td className="px-3 py-2" colSpan={5}>期末残高</td><td className="px-3 py-2 text-right font-numbers" data-testid="ledger-closing">{formatJPY(ledger.closing)}</td></tr></tfoot>
                </table>
              </div>
            )}
          </TabsContent>

          {/* P/L */}
          <TabsContent value="pl" className="mt-6 space-y-6">
            <div className="flex justify-end"><ExportBtns reportType="profit-loss" /></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard label="売上高" value={formatJPY(pl?.revenue)} accent="emerald" testid="pl-revenue" />
              <StatCard label="売上総利益" value={formatJPY(pl?.gross_profit)} accent="emerald" testid="pl-gross" />
              <StatCard label="費用合計" value={formatJPY(pl?.total_expenses)} accent="orange" testid="pl-expenses" />
              <StatCard label="当期純利益" value={formatJPY(pl?.net_profit)} accent={(pl?.net_profit || 0) >= 0 ? "emerald" : "orange"} testid="pl-profit" />
            </div>
            <BreakdownCard title="収益の内訳" rows={pl?.revenue_breakdown} total={pl?.revenue} color="bg-emerald-700" empty="収益の仕訳がありません" testidPrefix="pl-rev" />
            {pl?.cost_of_sales ? <BreakdownCard title="売上原価" rows={pl?.cost_breakdown} total={pl?.cost_of_sales} color="bg-amber-600" empty="—" testidPrefix="pl-cost" /> : null}
            <BreakdownCard title="販売費・一般管理費" rows={pl?.expenses_by_category} total={pl?.total_expenses} color="bg-orange-600" empty="費用の仕訳がありません" testidPrefix="pl-cat" />
          </TabsContent>

          {/* B/S */}
          <TabsContent value="bs" className="mt-6 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <BalancedBadge ok={bs?.balanced} />
              <ExportBtns reportType="balance-sheet" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <BSCard title="資産の部" items={bs?.assets.items} total={bs?.assets.total} testid="bs-assets-total" />
              <BSCard title="負債の部" items={bs?.liabilities.items} total={bs?.liabilities.total} testid="bs-liabilities-total" />
              <BSCard title="純資産の部" items={bs?.equity.items} total={bs?.equity.total} testid="bs-equity-total" />
            </div>
          </TabsContent>

          {/* 月次サマリ */}
          <TabsContent value="ms" className="mt-6 space-y-6">
            <div className="rounded-xl border border-stone-200 bg-white p-6">
              <h3 className="font-display font-semibold text-lg text-stone-900 mb-4">月次 収益・費用・純利益</h3>
              {!ms?.series?.length ? <p className="text-sm text-stone-500 py-8 text-center">仕訳データがありません</p> : (
                <div className="h-72"><ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ms.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
                    <XAxis dataKey="month" stroke="#a8a29e" fontSize={11} />
                    <YAxis stroke="#a8a29e" fontSize={11} tickFormatter={(v) => `¥${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatJPY(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e7e5e4", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" name="収益" fill="#064e3b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expense" name="費用" fill="#ea580c" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="net" name="純利益" fill="#0e7490" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer></div>
              )}
            </div>
            {ms?.series?.length ? (
              <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="monthly-table">
                  <thead className="bg-stone-50 text-stone-500 text-xs"><tr><th className="text-left px-3 py-2">月</th><th className="text-right px-3 py-2">仕訳数</th><th className="text-right px-3 py-2">借方計</th><th className="text-right px-3 py-2">貸方計</th><th className="text-right px-3 py-2">収益</th><th className="text-right px-3 py-2">費用</th><th className="text-right px-3 py-2">純利益</th></tr></thead>
                  <tbody>
                    {ms.series.map((s, i) => (
                      <tr key={i} className="border-t border-stone-100">
                        <td className="px-3 py-2 font-numbers">{s.month}</td>
                        <td className="px-3 py-2 text-right font-numbers text-stone-500">{s.entries}</td>
                        <td className="px-3 py-2 text-right font-numbers">{formatJPY(s.debit)}</td>
                        <td className="px-3 py-2 text-right font-numbers">{formatJPY(s.credit)}</td>
                        <td className="px-3 py-2 text-right font-numbers text-emerald-700">{formatJPY(s.revenue)}</td>
                        <td className="px-3 py-2 text-right font-numbers text-orange-700">{formatJPY(s.expense)}</td>
                        <td className="px-3 py-2 text-right font-numbers font-semibold">{formatJPY(s.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </TabsContent>

          {/* 消費税集計 */}
          <TabsContent value="ct" className="mt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard label="課税売上に係る消費税" value={formatJPY(ct?.sales?.tax_total)} accent="emerald" testid="ct-sales-tax" />
              <StatCard label="課税仕入に係る消費税" value={formatJPY(ct?.purchases?.tax_total)} accent="orange" testid="ct-purchase-tax" />
              <StatCard label="差引 納付税額(概算)" value={formatJPY(ct?.net_tax_payable)} accent={(ct?.net_tax_payable || 0) >= 0 ? "emerald" : "orange"} testid="ct-net" />
            </div>
            <p className="text-xs text-stone-400">売上=請求管理の請求書(税率別)、仕入=経費の消費税額から集計。本則課税の概算です。</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                <div className="px-4 py-3 bg-emerald-50 font-semibold text-emerald-900 text-sm">課税売上（税率別）</div>
                <table className="w-full text-sm" data-testid="ct-sales-table">
                  <thead className="bg-stone-50 text-stone-500 text-xs"><tr><th className="text-left px-3 py-2">税率区分</th><th className="text-right px-3 py-2">税抜金額</th><th className="text-right px-3 py-2">消費税額</th></tr></thead>
                  <tbody>
                    {ct?.sales?.by_rate?.length ? ct.sales.by_rate.map((r, i) => (
                      <tr key={i} className="border-t border-stone-100"><td className="px-3 py-2">{r.label}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(r.base)}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(r.tax)}</td></tr>
                    )) : <tr><td colSpan={3} className="text-center text-stone-400 py-8">課税売上がありません</td></tr>}
                  </tbody>
                  <tfoot className="bg-stone-50 font-semibold"><tr className="border-t-2 border-stone-300"><td className="px-3 py-2">合計</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(ct?.sales?.base_total)}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(ct?.sales?.tax_total)}</td></tr></tfoot>
                </table>
              </div>
              <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                <div className="px-4 py-3 bg-amber-50 font-semibold text-amber-900 text-sm">課税仕入（税率別）</div>
                <table className="w-full text-sm" data-testid="ct-purchase-table">
                  <thead className="bg-stone-50 text-stone-500 text-xs"><tr><th className="text-left px-3 py-2">税率区分</th><th className="text-right px-3 py-2">税抜金額</th><th className="text-right px-3 py-2">消費税額</th></tr></thead>
                  <tbody>
                    {ct?.purchases?.by_rate?.length ? ct.purchases.by_rate.map((r, i) => (
                      <tr key={i} className="border-t border-stone-100"><td className="px-3 py-2">{r.label}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(r.base)}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(r.tax)}</td></tr>
                    )) : <tr><td colSpan={3} className="text-center text-stone-400 py-8">課税仕入がありません</td></tr>}
                  </tbody>
                  <tfoot className="bg-stone-50 font-semibold"><tr className="border-t-2 border-stone-300"><td className="px-3 py-2">合計</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(ct?.purchases?.base_total)}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(ct?.purchases?.tax_total)}</td></tr></tfoot>
                </table>
              </div>
            </div>

            {/* 消費税申告書 (本則課税) */}
            <div className="rounded-xl border border-stone-200 bg-white overflow-hidden" data-testid="ctr-panel">
              <div className="px-4 py-3 bg-stone-800 text-white font-semibold text-sm flex items-center justify-between flex-wrap gap-2">
                <span>消費税申告書（本則課税・一般用） — 付表計算</span>
                <ExportBtns reportType="consumption-tax-return" disabled={!ctr} />
              </div>
              <div className="p-4 space-y-4">
                <div className="text-xs text-stone-500">課税売上割合 {ctr?.taxable_ratio}%{ctr?.full_deduction ? "（95%以上・全額控除）" : "（按分控除）"}・国税/地方内訳、端数処理（課税標準額=千円未満切捨／税額=百円未満切捨）を適用した概算値です。</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ctr-rate-table">
                    <thead className="bg-stone-50 text-stone-500 text-xs"><tr><th className="text-left px-3 py-2">税率区分</th><th className="text-right px-3 py-2">課税標準額</th><th className="text-right px-3 py-2">消費税額(国税)</th><th className="text-right px-3 py-2">控除対象仕入税額(国税)</th></tr></thead>
                    <tbody>
                      {ctr?.rows?.map((r, i) => (
                        <tr key={i} className="border-t border-stone-100"><td className="px-3 py-2">{r.label}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(r.tax_base)}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(r.national_output)}</td><td className="px-3 py-2 text-right font-numbers">{formatJPY(r.national_deduct)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard label="差引税額(国税)" value={formatJPY(ctr?.national_net)} accent="orange" testid="ctr-national" />
                  <StatCard label="地方消費税額" value={formatJPY(ctr?.local_tax)} accent="orange" testid="ctr-local" />
                  <StatCard label="納付税額 合計" value={formatJPY(ctr?.total_payable)} accent="emerald" testid="ctr-total" />
                  <StatCard label="課税売上割合" value={`${ctr?.taxable_ratio ?? 0}%`} accent="stone" testid="ctr-ratio" />
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="cf" className="mt-6 space-y-6">
            <p className="text-xs text-stone-400">現金・預金勘定の仕訳から算出</p>
            <div className="rounded-xl border border-stone-200 bg-white p-6">
              <h3 className="font-display font-semibold text-lg text-stone-900 mb-4">月次キャッシュフロー</h3>
              {!cf?.series?.length ? <p className="text-sm text-stone-500 py-8 text-center">データがありません</p> : (
                <div className="h-72"><ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cf.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
                    <XAxis dataKey="month" stroke="#a8a29e" fontSize={11} />
                    <YAxis stroke="#a8a29e" fontSize={11} tickFormatter={(v) => `¥${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatJPY(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e7e5e4", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="inflow" name="入金" fill="#064e3b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="outflow" name="出金" fill="#ea580c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer></div>
              )}
            </div>
            {cf?.series?.length ? (
              <div className="rounded-xl border border-stone-200 bg-white p-6">
                <h3 className="font-display font-semibold text-lg text-stone-900 mb-4">累計キャッシュ残高</h3>
                <div className="h-64"><ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cf.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                    <XAxis dataKey="month" stroke="#a8a29e" fontSize={11} />
                    <YAxis stroke="#a8a29e" fontSize={11} tickFormatter={(v) => `¥${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatJPY(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e7e5e4", fontSize: 12 }} />
                    <Line type="monotone" dataKey="cumulative" name="累計残高" stroke="#064e3b" strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer></div>
              </div>
            ) : null}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function BalancedBadge({ ok }) {
  return ok ? (
    <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-medium" data-testid="balanced-badge"><CheckCircle2 className="h-3.5 w-3.5" /> 貸借一致</div>
  ) : (
    <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1 text-xs font-medium" data-testid="balanced-badge"><AlertTriangle className="h-3.5 w-3.5" /> 貸借不一致（要確認）</div>
  );
}

function BreakdownCard({ title, rows, total, color, empty, testidPrefix }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6">
      <h3 className="font-display font-semibold text-lg text-stone-900 mb-4">{title}</h3>
      {!rows?.length ? <p className="text-sm text-stone-500 py-6 text-center">{empty}</p> : (
        <div className="space-y-2">
          {rows.map((c, idx) => {
            const pct = total ? (c.amount / total) * 100 : 0;
            return (
              <div key={idx} data-testid={`${testidPrefix}-${idx}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-stone-700">{c.name}</span>
                  <span className="font-numbers text-stone-900">{formatJPY(c.amount)} <span className="text-xs text-stone-500">({pct.toFixed(1)}%)</span></span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BSCard({ title, items, total, testid }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6">
      <h3 className="font-display font-semibold text-stone-900 mb-4">{title}</h3>
      {items?.length ? items.map((it, i) => <Row key={i} label={it.name} value={formatJPY(it.amount)} />) : <p className="text-sm text-stone-400 py-2">なし</p>}
      <Row label="合計" value={formatJPY(total)} bold testid={testid} />
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  const accentMap = { emerald: "border-emerald-200", orange: "border-orange-200", stone: "border-stone-200" };
  return (
    <div className={`rounded-xl border ${accentMap[accent]} bg-white p-5`} data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.1em] font-semibold text-stone-500">{label}</div>
      <div className="mt-2 font-numbers font-bold text-2xl text-stone-900">{value}</div>
    </div>
  );
}

function Row({ label, value, bold, testid }) {
  return (
    <div className={`flex justify-between py-2 border-b border-stone-100 last:border-b-0 ${bold ? "font-semibold text-stone-900 border-t-2 border-t-stone-300 pt-3 mt-2" : "text-stone-700"}`} data-testid={testid}>
      <span className="text-sm">{label}</span>
      <span className="font-numbers text-sm">{value}</span>
    </div>
  );
}
